<script setup lang="ts">
import { useTeamStore } from "@/stores/teams.ts";
import { onMounted, ref, computed } from "vue";
import TeamDisplay from "@/components/TeamDisplay.vue";

const teamStore = useTeamStore();
const ishdTeamList = ref<any[]>([]);
const ishdTeamSearch = ref("");
const filteredIshdTeams = computed(() => {
  return ishdTeamList.value
    .filter(team => team.name.toLowerCase().includes(ishdTeamSearch.value.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name)).slice(0, 10);
});

const newTeam = ref({
  name: "",
  logo_url: "",
});

function ishdTeamLogo(team: any) {
  return team._links?.logo?.href ?
    `https://www.ishd.de${team._links?.logo?.href}?width=250&height=250` :
    "https://www.ishd.de/img/logos/ishd.png";
}

async function addIshdTeam(team: any) {
  const newTeamData = {
    name: team.name,
    logo_url: ishdTeamLogo(team),
  };
  await teamStore.putTeam(newTeamData);
  newTeam.value = { name: "", logo_url: "" }; // Reset the form
}

async function getIshdTeamList() {
  const response = await fetch("/api/teams/ishd");
  if (!response.ok) {
    throw new Error("Failed to fetch ISHD team list");
  }
  const data = await response.json();
  ishdTeamList.value = data._embedded.clubs;
}

function onSubmit() {
  teamStore.putTeam(newTeam.value);
  newTeam.value = { name: "", logo_url: "" }; // Reset the form
}

onMounted(() => {
  getIshdTeamList();
});

</script>

<template>
  <div class="page">
    <div class="team-edit panel">
      <h2>Teams bearbeiten</h2>
      <table>
        <thead>
          <tr>
            <th>Logo</th>
            <th>Name</th>
            <th>LogoUrl</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="team in teamStore.teams" :key="team.id">
            <td>
              <TeamDisplay :team="team" hide_name class="score-display" />
            </td>
            <td>
              <input class="l2" v-model="team.name" placeholder="Team Name" />
            </td>
            <td>
              <input class="l2" v-model="team.logo_url" placeholder="Logo URL" />
            </td>
            <td>
              <div class="score-display">
                <button class="l2" @click="teamStore.putTeam(team)">Save</button>
                <button class="circle danger" @click="teamStore.deleteTeam(team)">X</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="team-create panel">
      <h2>Neues Team erstellen</h2>
      <img class="preview" :src="newTeam.logo_url || 'https://www.ishd.de/img/logos/ishd.png'" />
      <form @submit.prevent="onSubmit">
        <input class="l2" v-model="newTeam.name" placeholder="Team Name" />
        <input class="l2" v-model="newTeam.logo_url" placeholder="Logo URL" />
        <button class="l2" type="submit">Hinzufügen</button>
      </form>
    </div>
    <div class="ishd-teams panel">
      <h2>ISHD Teams hinzufügen</h2>
      <input class="l2" v-model="ishdTeamSearch" placeholder="Suche" />
      <div class="panel deep ishd-teams-list">
        <ul>
          <li v-for="team in filteredIshdTeams" :key="team.id">
            <button class="circle" @click="addIshdTeam(team)">+</button>
            <TeamDisplay :team="{ name: team.name, logo_url: ishdTeamLogo(team) }" />
          </li>
        </ul>
        <p class="not-found" v-if="filteredIshdTeams.length === 0">Keine ISHD Teams gefunden</p>
      </div>
    </div>
  </div>
</template>

<style scoped>

.team-edit {
  td {
    text-align: center;
    vertical-align: middle;

    input {
      width: 100%;
    }
  }

  .score-display {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
  }
}

.team-create {
  img.preview {
    width: 5rem;
    height: 5rem;
    border-radius: 50%;
    object-fit: contain;
    align-self: center;
    background-color: white;
  }

  form {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
}

.ishd-teams-list {
  display: flex;
  min-height: 22rem;

  ul {
    list-style: none;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  li {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    border-radius: 4px;
  }

  .not-found {
    width: 100%;
    text-align: center;
    align-self: center;
  }
}

</style>
